import { useTranslation } from '@rhino-project/core/i18n';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

import {
  ModelFilters,
  ModelIndexSimple,
  ModelSearch,
  IconButton
} from '@rhino-project/ui-heroui';
import {
  AlertDialog,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
  Dropdown,
  DropdownTrigger,
  DropdownPopover,
  DropdownMenu,
  DropdownItem,
  Button,
  Separator
} from '@heroui/react';
import { Icon } from '@iconify/react';
import {
  useBaseOwnerId,
  useModelInvalidateIndex,
  useModelOptimisticUpdate,
  useModelShow,
  useModelUpdate
} from '@rhino-project/core/hooks';
import { useHasRoleOf } from '@rhino-project/core/hooks';
import { useModelIndexContext } from '@rhino-project/core/hooks';
import { useActiveSeasons, usePlanningSeasons } from '../../hooks/ots';
import { useMode } from '../../hooks/useMode';
import { useRosterStatuses } from '../../hooks/rosterstatus';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  PositionsChip,
  RatingChip,
  InlineEditChip
} from '../shared/PlayerChips';
import ReportSidebarForm from '../report/ReportSidebarForm';
import RecruitingPlayerAdd from './RecruitingPlayerAdd';
import { PlayerDetailLink } from './PlayerDetailLink';
import type { RhinoRead } from '@rhino-project/core';

type RosterStatus = RhinoRead<'roster_status'>;
type TeamSeason = RhinoRead<'team_season'>;
type Roster = RhinoRead<'roster'>;

const DRAG_TYPE = 'application/x-roster-id';

interface TouchDragState {
  startX: number;
  startY: number;
  rosterId: unknown;
  dragging: boolean;
}

interface RecruitCardMenuProps {
  roster: Roster;
  onAction: (action: string, rosterId: unknown) => void;
}

const RecruitCardMenu = ({ roster, onAction }: RecruitCardMenuProps) => {
  const { t } = useTranslation();

  const { owner } = useParams({ from: '/_authenticated/$owner' });
  const { results: planningSeasons } = usePlanningSeasons();
  const { mutate } = useModelUpdate('roster');
  const teamSeason = roster.team_season;
  const player = roster.player;

  const transferSeasons = useMemo(
    () =>
      (planningSeasons as TeamSeason[] | undefined)?.filter(
        (ts) => ts.id !== teamSeason.id
      ) || [],
    [planningSeasons, teamSeason.id]
  );

  const handleAction = useCallback(
    (key: string) => {
      if (key === 'archive') {
        onAction('archive', roster.id);
      } else if (key === 'report') {
        onAction('report', roster.id);
      } else if (key.startsWith('transfer-')) {
        const tsId = parseInt(key.replace('transfer-', ''), 10);
        mutate({ ...roster, team_season: tsId } as unknown as Parameters<
          typeof mutate
        >[0]);
      }
    },
    [onAction, roster, mutate]
  );

  return (
    <Dropdown>
      <DropdownTrigger className="button--ghost min-w-0 px-1 py-0 h-auto">
        <Icon icon="bi:three-dots-vertical" width="18" height="18" />
      </DropdownTrigger>
      <DropdownPopover>
        <DropdownMenu aria-label={t('frontend.messages.card_actions')}>
          <DropdownItem
            id="view-player"
            textValue={t('frontend.messages.view_player')}
          >
            <PlayerDetailLink
              owner={owner}
              teamSeason={teamSeason}
              playerId={String(player.id)}
            >
              {t('frontend.messages.view_player')}
            </PlayerDetailLink>
          </DropdownItem>
          <DropdownItem
            id="report"
            textValue={t('frontend.messages.add_report_2')}
            onAction={() => handleAction('report')}
          >
            {t('frontend.messages.add_report_2')}
          </DropdownItem>
          <Separator />
          {transferSeasons.map((ts) => (
            <DropdownItem
              key={`transfer-${ts.id}`}
              id={`transfer-${ts.id}`}
              textValue={ts.display_name as string}
              onAction={() => handleAction(`transfer-${ts.id}`)}
            >
              {t('frontend.messages.move_to')}
              {ts.display_name as string}
            </DropdownItem>
          ))}
          <Separator />
          <DropdownItem
            id="archive"
            textValue={t('frontend.messages.archive')}
            onAction={() => handleAction('archive')}
            className="text-danger"
          >
            {t('frontend.messages.archive')}
          </DropdownItem>
        </DropdownMenu>
      </DropdownPopover>
    </Dropdown>
  );
};

interface RecruitCardProps {
  roster: Roster;
  onAction: (action: string, rosterId: unknown) => void;
  isAdmin?: boolean;
  onPlayerUpdated?: () => void;
}

const RecruitCard = ({
  roster,
  onAction,
  isAdmin,
  onPlayerUpdated
}: RecruitCardProps) => {
  const { t } = useTranslation();

  const { owner } = useParams({ from: '/_authenticated/$owner' });
  const player = roster.player as RhinoRead<'player'>;
  const teamSeason = roster.team_season;
  const rosterStatus = roster.roster_status;

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.dataTransfer.setData(DRAG_TYPE, String(roster.id));
      e.dataTransfer.effectAllowed = 'move';
    },
    [roster.id]
  );

  // Touch drag support
  const touchRef = useRef<TouchDragState | null>(null);
  const touchCloneRef = useRef<HTMLElement | null>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (!isAdmin) return;
      const touch = e.touches[0];
      touchRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        rosterId: roster.id,
        dragging: false
      };
    },
    [isAdmin, roster.id]
  );

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLElement>) => {
    if (!touchRef.current) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchRef.current.startX);
    const dy = Math.abs(touch.clientY - touchRef.current.startY);

    if (!touchRef.current.dragging && (dx > 10 || dy > 10)) {
      touchRef.current.dragging = true;
      // Create visual clone
      const el = e.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.position = 'fixed';
      clone.style.width = `${rect.width}px`;
      clone.style.opacity = '0.8';
      clone.style.pointerEvents = 'none';
      clone.style.zIndex = '9999';
      document.body.appendChild(clone);
      touchCloneRef.current = clone;
      el.style.opacity = '0.3';
    }

    if (touchRef.current.dragging) {
      e.preventDefault();
      if (touchCloneRef.current) {
        touchCloneRef.current.style.left = `${touch.clientX - 50}px`;
        touchCloneRef.current.style.top = `${touch.clientY - 20}px`;
      }
      // Highlight lane under touch
      const elUnder = document.elementFromPoint(touch.clientX, touch.clientY);
      const lane = elUnder?.closest('[data-lane-id]');
      document.querySelectorAll('[data-lane-id]').forEach((l) => {
        l.classList.toggle('ring-2', l === lane);
        l.classList.toggle('ring-accent', l === lane);
      });
    }
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLElement>) => {
    if (!touchRef.current) return;
    const wasDragging = touchRef.current.dragging;
    const rosterId = touchRef.current.rosterId;

    // Clean up clone
    if (touchCloneRef.current) {
      touchCloneRef.current.remove();
      touchCloneRef.current = null;
    }
    (e.currentTarget as HTMLElement).style.opacity = '';

    // Remove all highlights
    document.querySelectorAll('[data-lane-id]').forEach((l) => {
      l.classList.remove('ring-2', 'ring-accent');
    });

    if (wasDragging) {
      const touch = e.changedTouches[0];
      const elUnder = document.elementFromPoint(touch.clientX, touch.clientY);
      const lane = elUnder?.closest('[data-lane-id]') as HTMLElement | null;
      if (lane) {
        const laneId = parseInt(lane.dataset.laneId!, 10);
        // Dispatch custom event for the board to handle
        window.dispatchEvent(
          new CustomEvent('kanban-drop', {
            detail: { rosterId, rosterStatusId: laneId }
          })
        );
      }
    }

    touchRef.current = null;
  }, []);

  const externalLinkedTeams = player?.external_linked_teams;

  return (
    <div
      draggable={isAdmin}
      onDragStart={handleDragStart}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`rounded-lg border border-border-secondary bg-surface shadow-surface my-2 p-3 ${
        isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-1">
        <PlayerDetailLink
          owner={owner}
          teamSeason={teamSeason}
          playerId={String(player.id)}
          className="font-semibold text-sm hover:underline"
        >
          {player.display_name}
        </PlayerDetailLink>
        <div className="flex items-center gap-1 shrink-0">
          <RatingChip
            rating={roster?.quick_rating as number | null}
            size="xs"
          />
          {isAdmin && <RecruitCardMenu roster={roster} onAction={onAction} />}
        </div>
      </div>
      {(rosterStatus.status_type as string) !== 'active' && (
        <div className="text-muted text-xs mt-0.5">
          {(externalLinkedTeams?.[0]?.display_name as string) ||
            t('frontend.messages.unknown')}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-1.5">
        <PositionsChip
          player={player}
          isAdmin={isAdmin}
          onSaved={onPlayerUpdated}
          size="xs"
        />
        <InlineEditChip
          value={player.dob_year as number | null}
          label={t('frontend.messages.born')}
          field="dob_year"
          playerId={player.id as number}
          isAdmin={isAdmin}
          onSaved={onPlayerUpdated}
          size="xs"
        />
        <InlineEditChip
          value={player.grad_year as number | null}
          label={t('frontend.messages.grad')}
          field="grad_year"
          playerId={player.id as number}
          isAdmin={isAdmin}
          onSaved={onPlayerUpdated}
          size="xs"
        />
      </div>
    </div>
  );
};

interface RecruitLaneProps {
  rosterStatus: RosterStatus;
  rosters: Roster[];
  onCardDrop: (rosterStatus: RosterStatus, rosterId: number) => void;
  onRecruitAdd: (rosterStatus: RosterStatus) => void;
  onCardAction: (
    action: string,
    rosterStatus: RosterStatus,
    rosterId: unknown
  ) => void;
  isAdmin?: boolean;
  onPlayerUpdated?: () => void;
}

const RecruitLane = ({
  rosterStatus,
  rosters,
  onCardDrop,
  onRecruitAdd,
  onCardAction,
  isAdmin,
  onPlayerUpdated
}: RecruitLaneProps) => {
  const { t } = useTranslation();

  const [isOver, setIsOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setIsOver(false);
      const rosterId = e.dataTransfer.getData(DRAG_TYPE);
      if (rosterId) {
        onCardDrop(rosterStatus, parseInt(rosterId, 10));
      }
    },
    [onCardDrop, rosterStatus]
  );

  const headerText = `${rosterStatus.name} (${rosters.length})`;

  return (
    <div
      className="flex-1 mx-1 flex flex-col min-h-0"
      data-lane-id={rosterStatus.id as number}
    >
      {/* rounded-3xl matches HeroUI Card default radius */}
      <div className="flex items-center justify-between p-2 bg-surface-secondary rounded-t-3xl">
        <Typography.Heading level={6}>{headerText}</Typography.Heading>
        {isAdmin && (
          <Button
            isIconOnly
            variant="ghost"
            size="sm"
            aria-label={t('frontend.messages.add_player_to', {
              status: rosterStatus.name
            })}
            onPress={() => onRecruitAdd(rosterStatus)}
          >
            <Icon icon="bi:plus-circle-fill" width="18" height="18" />
          </Button>
        )}
      </div>
      <div
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex-1 overflow-y-auto min-h-0 p-1 rounded-b-3xl transition-colors ${
          isOver ? 'bg-accent/10 ring-2 ring-accent' : 'bg-surface-secondary/50'
        }`}
      >
        {rosters.map((r) => (
          <RecruitCard
            key={r.id as number}
            roster={r}
            isAdmin={isAdmin}
            onPlayerUpdated={onPlayerUpdated}
            onAction={(action: string, rosterId: unknown) =>
              onCardAction(action, rosterStatus, rosterId)
            }
          />
        ))}
      </div>
    </div>
  );
};

interface RecruitingBoardProps {
  team_season: TeamSeason | null | undefined;
}

const RecruitingBoard = ({ team_season }: RecruitingBoardProps) => {
  const { isLoading: loading, resources } = useModelIndexContext();
  const [modalOpen, setModalOpen] = useState<string | false>(false);
  // Seed values a create form opens with, not a player the API returned.
  const [rosterPlayer, setRosterPlayer] = useState<object | null>(null);
  const [reportPlayerId, setReportPlayerId] = useState<number | null>(null);
  const { invalidate: rosterInvalidate } = useModelInvalidateIndex('roster');
  const isAdmin = useHasRoleOf('admin');

  const { resources: rosterStatuses } = useRosterStatuses({
    status_type: team_season?.status
  });

  const { resources: archivedStatuses } = useRosterStatuses({
    status_type: 'archived'
  });

  const { mutate: rosterUpdate } = useModelOptimisticUpdate('roster');

  const rosterStatusResults = (
    rosterStatuses as { results: RosterStatus[] } | undefined
  )?.results;
  const resourceResults = (resources as { results: Roster[] } | undefined)
    ?.results;
  const archivedResults = (
    archivedStatuses as { results: RosterStatus[] } | undefined
  )?.results;

  const columns = useMemo(
    () =>
      rosterStatusResults?.map((rosterStatus) => ({
        rosterStatus,
        rosters:
          resourceResults?.filter(
            (r) => r.roster_status.name === rosterStatus.name
          ) || []
      })),
    [rosterStatusResults, resourceResults]
  );

  // Listen for touch-based kanban drops
  const handleCardDrop = useCallback(
    (rosterStatus: RosterStatus, rosterId: number) => {
      rosterUpdate({ id: rosterId, roster_status: rosterStatus });
    },
    [rosterUpdate]
  );

  // Touch drop handler via custom event
  useEffect(() => {
    const handler = (e: Event) => {
      const { rosterId, rosterStatusId } = (e as CustomEvent).detail;
      // The event carries the lane's id; the patch merges into cached rows, so
      // it needs the record the read carries rather than a reference to it.
      const rosterStatus = rosterStatusResults?.find(
        (rs) => String(rs.id) === String(rosterStatusId)
      );
      if (rosterStatus) handleCardDrop(rosterStatus, Number(rosterId));
    };
    window.addEventListener('kanban-drop', handler);
    return () => window.removeEventListener('kanban-drop', handler);
  }, [handleCardDrop, rosterStatusResults]);

  if (!team_season || !rosterStatuses || !archivedStatuses || loading)
    return <Spinner size="sm" className="mx-auto block" />;

  const handleModalClose = () => {
    setModalOpen(false);
    setRosterPlayer(null);
    rosterInvalidate();
  };

  const handleRecruitAdd = (rosterStatus: RosterStatus) => {
    setModalOpen('player');
    setRosterPlayer({
      organization: team_season.team.organization,
      rosters: [{ roster_status: rosterStatus, team_season: team_season }]
    });
  };

  const handleCardAction = (
    action: string,
    _rosterStatus: RosterStatus,
    rosterId: unknown
  ) => {
    const archivedStatus = archivedResults?.[0];

    switch (action) {
      case 'archive':
        if (archivedStatus)
          rosterUpdate({ id: Number(rosterId), roster_status: archivedStatus });
        break;
      case 'report': {
        const roster = resourceResults?.find((r) => r.id === rosterId);
        const player = roster?.player;
        if (player?.id) setReportPlayerId(player.id as number);
        break;
      }
      default:
        console.warn('unknown action!');
    }
  };

  return (
    <>
      <div className="flex gap-2 flex-1 min-h-0">
        {columns?.map(({ rosterStatus: rs, rosters }) => (
          <RecruitLane
            key={rs.id as number}
            rosterStatus={rs}
            rosters={rosters}
            isAdmin={isAdmin}
            onCardAction={handleCardAction}
            onCardDrop={handleCardDrop}
            onRecruitAdd={handleRecruitAdd}
            onPlayerUpdated={rosterInvalidate}
          />
        ))}
      </div>
      <ReportSidebarForm
        resource={{ player: reportPlayerId }}
        isOpen={!!reportPlayerId}
        parentId={reportPlayerId}
        onModalClose={() => {
          setReportPlayerId(null);
          rosterInvalidate();
        }}
      />
      <RecruitingPlayerAdd
        isOpen={modalOpen === 'player'}
        onModalClose={handleModalClose}
        extraDefaultValues={rosterPlayer ?? undefined}
      />
    </>
  );
};

interface SeasonButtonProps {
  team_season: TeamSeason | null | undefined;
}

type SeasonAction = 'activate' | 'archive';

const SeasonButton = ({ team_season }: SeasonButtonProps) => {
  const { t } = useTranslation();

  const { results } = useModelIndexContext() as {
    results: Roster[] | undefined;
  };
  const isAdmin = useHasRoleOf('admin');
  const navigate = useNavigate();
  const baseOwnerId = useBaseOwnerId();
  const { setMode } = useMode();
  const [pendingAction, setPendingAction] = useState<SeasonAction | null>(null);

  const isPlanning = team_season?.status === 'planning';

  const { resources: rosterStatuses } = useRosterStatuses({
    status_type: team_season?.status
  });

  const { resources: activeSeasons } = useActiveSeasons();

  const {
    mutate: updateTeamSeason,
    error: updateError,
    reset: resetUpdate,
    isPending: isUpdating
  } = useModelUpdate('team_season');

  const rosterStatusResults = (
    rosterStatuses as { results: RosterStatus[] } | undefined
  )?.results;

  const lastStatus = useMemo(() => {
    const slice = rosterStatusResults?.slice(-1) || [];
    return slice[0];
  }, [rosterStatusResults]);

  const canActivate = useMemo(() => {
    if (!isAdmin || !isPlanning) return false;
    if (!lastStatus) return false;
    return results?.every(
      (roster) => roster.roster_status.name === lastStatus?.name
    );
  }, [isAdmin, isPlanning, lastStatus, results]);

  if (!team_season || team_season?.status === 'archived') return null;

  const seasonName =
    team_season.season?.name || t('frontend.messages.this_season_2');

  const activeSeasonsResults =
    (activeSeasons as { results: TeamSeason[] } | undefined)?.results || [];
  const activeSeason = activeSeasonsResults[0]?.season;
  const thisSeasonStartsOn = team_season.season?.starts_on?.slice(0, 10);
  const activeSeasonStartsOn = activeSeason?.starts_on?.slice(0, 10);

  // A planning season can only be archived directly once a newer season is
  // already active -- otherwise it's still the natural next plan, not a
  // stale/abandoned one. No active season at all means there's nothing
  // newer to defer to, so archiving stays allowed. Active-status seasons
  // are unaffected (they could always be archived directly).
  const canArchive =
    isAdmin &&
    (!isPlanning ||
      !activeSeasonStartsOn ||
      !thisSeasonStartsOn ||
      activeSeasonStartsOn > thisSeasonStartsOn);

  // Only warn about the auto-archive when it's surprising: activating an
  // older season while a newer one is currently active would silently
  // archive that newer season, which is easy to do by mistake.
  const willArchiveActive =
    isPlanning &&
    !!activeSeasonStartsOn &&
    !!thisSeasonStartsOn &&
    activeSeasonStartsOn > thisSeasonStartsOn;
  const activeSeasonName = (activeSeason?.name as string | undefined) || '';

  const isActivating = pendingAction === 'activate';
  const dialogButtonText = isActivating
    ? t('frontend.messages.make_season_current')
    : t('frontend.messages.archive_season');

  const openModal = (action: SeasonAction) => {
    resetUpdate();
    setPendingAction(action);
  };

  const handleConfirm = () => {
    updateTeamSeason(
      {
        ...team_season,
        status: isActivating ? 'active' : 'archived'
      } as Parameters<typeof updateTeamSeason>[0],
      {
        onSuccess: () => {
          setPendingAction(null);
          if (isActivating) {
            setMode('season');
            navigate({
              to: `/${baseOwnerId}/active/${team_season.id as number | string}`
            });
          } else {
            setMode('plan');
            navigate({ to: `/${baseOwnerId}/plan` });
          }
        }
      }
    );
  };

  return (
    <div className="flex flex-wrap justify-between mb-3">
      <div className="flex flex-wrap gap-2">
        {canArchive && (
          <IconButton
            variant="danger-soft"
            icon="bi:archive"
            onClick={() => openModal('archive')}
          >
            {t('frontend.messages.archive_season')}
          </IconButton>
        )}
        {isPlanning && (
          <Tooltip delay={300} closeDelay={0}>
            <TooltipTrigger>
              <span id="activate-season-status" className="inline-block">
                <IconButton
                  color="primary"
                  icon="bi:arrow-right-short"
                  onClick={() => {
                    if (canActivate) openModal('activate');
                  }}
                  aria-disabled={!canActivate}
                  className={
                    !canActivate ? 'opacity-60 cursor-not-allowed' : undefined
                  }
                >
                  {t('frontend.messages.make_season_current')}
                </IconButton>
              </span>
            </TooltipTrigger>
            {!canActivate && lastStatus && (
              <TooltipContent className="break-normal">
                {t('frontend.messages.activate_requirement', {
                  status: lastStatus.name
                })}
              </TooltipContent>
            )}
          </Tooltip>
        )}
      </div>
      <AlertDialog
        isOpen={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.CloseTrigger />
              <AlertDialog.Header>
                <AlertDialog.Icon
                  status={isActivating ? 'accent' : 'warning'}
                />
                <AlertDialog.Heading>{dialogButtonText}</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                {isActivating ? (
                  <p>
                    {t('frontend.messages.activate_confirmation', {
                      season: seasonName
                    })}
                  </p>
                ) : (
                  <p>
                    {t('frontend.messages.archive_confirmation', {
                      season: seasonName
                    })}
                  </p>
                )}
                {willArchiveActive && isActivating && (
                  <p className="mt-2 text-warning">
                    {t('frontend.messages.season_will_archive', {
                      season: activeSeasonName
                    })}
                  </p>
                )}
                {updateError && (
                  <p className="mt-2 text-danger">
                    {t('frontend.messages.error_updating_season')}
                  </p>
                )}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="ghost"
                  onPress={() => setPendingAction(null)}
                  isDisabled={isUpdating}
                >
                  {t('frontend.messages.cancel')}
                </Button>
                <Button
                  variant={isActivating ? 'primary' : 'danger'}
                  onPress={handleConfirm}
                  isDisabled={isUpdating}
                >
                  {isUpdating
                    ? t('frontend.messages.saving')
                    : dialogButtonText}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
};

interface RecruitingProps {
  status_type?: string;
}

const Recruiting = ({ status_type = 'active' }: RecruitingProps) => {
  const { team_season_id } = useParams({ strict: false });
  const defaultFilter = useMemo(
    () => ({
      team_season_id,
      roster_status: { status_type }
    }),
    [team_season_id, status_type]
  );

  const { resource: team_season } = useModelShow(
    'team_season',
    team_season_id!
  );

  return (
    <div
      key={team_season_id as string}
      className="flex-1 flex flex-col min-h-0"
    >
      <ModelIndexSimple
        analyticsSurface="recruiting"
        model="roster"
        defaultOrder={[
          '-quick_rating',
          'player.last_name',
          'player.first_name'
        ]}
        defaultFilter={defaultFilter}
        defaultLimit={10000}
      >
        <ModelSearch />
        <ModelFilters
          paths={[
            'player.player_positions.position::tree_subtree',
            'player.dob_year',
            'player.grad_year'
          ]}
        />
        <SeasonButton team_season={team_season} />
        <RecruitingBoard team_season={team_season} />
      </ModelIndexSimple>
    </div>
  );
};

export default Recruiting;
